import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const emptyQ = { question: "", options: ["", "", "", ""], correct_option: 0 };
const empty = () => ({
  title: "", description: "", exam_date: "", result_date: "",
  duration_minutes: 30,
  price: 0, is_free: true, is_published: true, pass_mark_percent: 40,
  questions: [{ ...emptyQ }],
});

export default function AdminTestSeries() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/test-series");
    setItems(r.data || []);
  };
  const save = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price) || 0,
        duration_minutes: Number(editing.duration_minutes) || 30,
        pass_mark_percent: Number(editing.pass_mark_percent) || 40,
        questions: editing.questions.map((q) => ({
          question: q.question,
          options: q.options,
          correct_option: Number(q.correct_option) || 0,
        })),
      };
      if (editing.id) await api.put(`/admin/test-series/${editing.id}`, payload);
      else await api.post("/admin/test-series", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/test-series/${id}`);
    toast.success("Deleted");
    load();
  };
  const publishAll = async (id) => {
    if (!window.confirm("Declare results for ALL students of this test?")) return;
    try {
      const { data } = await api.put(`/admin/test-series/${id}/publish-all-results`);
      toast.success(`Results published for ${data.updated} attempt(s)`);
    } catch { toast.error("Failed"); }
  };

  const setQuestion = (idx, patch) => {
    const qs = [...editing.questions];
    qs[idx] = { ...qs[idx], ...patch };
    setEditing({ ...editing, questions: qs });
  };
  const setOption = (qi, oi, val) => {
    const qs = [...editing.questions];
    qs[qi].options[oi] = val;
    setEditing({ ...editing, questions: qs });
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl font-bold">Test Series</h1>
        <button onClick={() => setEditing(empty())} className="bg-brand text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1" data-testid="new-test-button">
          <Plus size={16} /> New Test
        </button>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
            <BookOpen size={36} className="mx-auto text-slate-300" />
            <div className="mt-3 text-slate-500">No test series yet.</div>
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`admin-test-${t.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded ${t.is_free ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {t.is_free ? "Free" : `₹${t.price}`}
              </span>
              <span className="text-xs text-slate-500">{t.duration_minutes} min · {t.questions?.length || 0} Q</span>
            </div>
            <h3 className="heading text-lg font-bold mt-2">{t.title}</h3>
            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.description}</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={() => setEditing(t)} className="text-xs font-semibold bg-slate-100 hover:bg-slate-200 rounded px-3 py-1.5 inline-flex items-center gap-1" data-testid={`edit-test-${t.id}`}><Edit size={12} /> Edit</button>
              <button onClick={() => publishAll(t.id)} className="text-xs font-semibold bg-brand text-white rounded px-3 py-1.5" data-testid={`publish-all-${t.id}`}>Declare Results</button>
              <button onClick={() => del(t.id)} className="text-xs font-semibold text-red-600 hover:bg-red-50 rounded px-3 py-1.5 inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Create"} Test Series</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Inp label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="test-title-input" />
                <Inp label="Duration (min)" type="number" v={editing.duration_minutes} on={(v) => setEditing({ ...editing, duration_minutes: v })} test="test-duration-input" />
                <Inp label="Price (₹)" type="number" v={editing.price} on={(v) => setEditing({ ...editing, price: v })} test="test-price-input" />
                <Inp label="Pass Mark (%)" type="number" v={editing.pass_mark_percent} on={(v) => setEditing({ ...editing, pass_mark_percent: v })} test="test-passmark-input" />
                <Inp label="Exam Date" type="datetime-local" v={editing.exam_date || ""} on={(v) => setEditing({ ...editing, exam_date: v })} test="test-date-input" />
                <Inp label="Result Date" type="datetime-local" v={editing.result_date || ""} on={(v) => setEditing({ ...editing, result_date: v })} test="test-result-date-input" />
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_free} onChange={(e) => setEditing({ ...editing, is_free: e.target.checked })} className="accent-brand" /> Free</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} className="accent-brand" /> Published</label>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Description</label>
                <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none" data-testid="test-desc-input" />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold">Questions ({editing.questions.length})</div>
                <button onClick={() => setEditing({ ...editing, questions: [...editing.questions, { ...emptyQ }] })} className="text-xs bg-brand text-white rounded px-3 py-1.5 font-semibold" data-testid="add-question-button">+ Add Question</button>
              </div>
              {editing.questions.map((q, qi) => (
                <div key={qi} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Q {qi + 1}</div>
                    <button onClick={() => setEditing({ ...editing, questions: editing.questions.filter((_, i) => i !== qi) })} className="text-red-600 text-xs">Remove</button>
                  </div>
                  <input value={q.question} onChange={(e) => setQuestion(qi, { question: e.target.value })} placeholder="Question" className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white" data-testid={`q-${qi}-text`} />
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {q.options.map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="radio" checked={Number(q.correct_option) === oi} onChange={() => setQuestion(qi, { correct_option: oi })} className="accent-brand" data-testid={`q-${qi}-correct-${oi}`} />
                        <input value={o} onChange={(e) => setOption(qi, oi, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oi)}`} className="flex-1 px-3 py-2 rounded-lg border border-slate-300 bg-white" data-testid={`q-${qi}-opt-${oi}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancel</button>
            <button onClick={save} className="bg-brand text-white font-semibold rounded-lg px-5 py-2 text-sm" data-testid="test-save-button">Save Test</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Inp({ label, v, on, type = "text", test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-brand" data-testid={test} />
    </div>
  );
}
